<template>
    <div
        class="max-w-md mx-auto bg-white shadow-lg rounded-2xl overflow-hidden border border-gray-100 p-4 font-sans text-gray-700">

        <div class="flex items-center justify-between mb-5 px-1 text-sm">
            <div class="flex items-center gap-2">
                <select v-model="currentYear" @change="updateCalendar"
                    class="border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none font-medium">
                    <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}年</option>
                </select>
            </div>
            <div class="flex items-center gap-1.5">
                <div class="flex items-center">
                    <button @click="prevMonth" class="p-1.5 text-gray-400 font-bold hover:text-blue-600">&lt;</button>
                    <select v-model="currentMonth" @change="updateCalendar"
                        class="border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none font-medium w-16">
                        <option v-for="m in 12" :key="m" :value="m">{{ m }}月</option>
                    </select>
                    <button @click="nextMonth" class="p-1.5 text-gray-400 font-bold hover:text-blue-600">&gt;</button>
                </div>
            </div>
            <button @click="goToday"
                class="px-5 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition">今天</button>
        </div>

        <div class="grid grid-cols-7 mb-1 text-center border-b border-gray-50 pb-2">
            <div v-for="(w, index) in weeks" :key="w" class="text-xs font-normal"
                :class="(index === 5 || index === 6) ? 'text-red-500' : 'text-gray-400'">
                {{ w }}
            </div>
        </div>

        <div class="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-md overflow-hidden">
            <div v-for="(item, index) in calendarDays" :key="index" @click="selectDay(item)"
                class="relative h-16 bg-white flex flex-col items-center justify-center cursor-pointer transition-all"
                :class="[
                    !item.isCurrentMonth ? 'opacity-50' : 'hover:bg-blue-50',
                    selectedDate === item.fullDate ? 'z-20 ring-2 ring-blue-600 ring-inset ring-offset-0 shadow-sm font-bold' : ''
                ]">
                <span v-if="item.isToday"
                    class="absolute top-0 left-0 text-[9px] w-4 h-4 flex items-center justify-center bg-green-500 text-white rounded-br-sm font-bold scale-90">今</span>

                <span v-if="item.isRest"
                    class="absolute top-1 right-1 text-[9px] w-4 h-4 flex items-center justify-center bg-red-500 text-white rounded-sm font-bold scale-90">休</span>
                <span v-if="item.isWork"
                    class="absolute top-1 right-1 text-[9px] w-4 h-4 flex items-center justify-center bg-gray-700 text-white rounded-sm font-bold scale-90">班</span>

                <span class="text-xl" :class="[
                    selectedDate === item.fullDate ? '' :
                        (item.isToday ? 'text-blue-600 font-bold' : (index % 7 >= 5 ? 'text-red-500' : 'text-gray-700'))
                ]">
                    {{ item.day }}
                </span>

                <span class="text-[10px] mt-0.5 truncate w-full text-center px-1 font-light" :class="[
                    selectedDate === item.fullDate ? 'text-blue-600' :
                        (item.isFestival ? 'text-red-400' : (item.isToday ? 'text-blue-400' : 'text-gray-400'))
                ]">
                    {{ item.lunarText }}
                </span>
            </div>
        </div>

        <div class="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100" v-if="activeInfo">
            <div class="flex items-start gap-4">
                <div class="text-left flex-shrink-0">
                    <div class="text-base font-bold text-gray-800">{{ activeInfo.lunarMonth }}月{{ activeInfo.lunarDay }}
                    </div>
                    <div class="text-[11px] text-gray-400 mt-0.5">{{ activeInfo.ganZhi }}年 属{{ activeInfo.zodiac }}
                    </div>
                </div>

                <div class="flex-1 min-w-0 space-y-2 border-l border-gray-200 pl-4">
                    <div class="flex gap-2 items-center text-[11px]">
                        <span
                            class="flex-shrink-0 bg-orange-500 text-white w-5 h-5 flex items-center justify-center rounded font-medium">宜</span>
                        <p class="text-gray-600 truncate flex-1" :title="activeInfo.suit">
                            {{ activeInfo.suit }}
                        </p>
                    </div>

                    <div class="flex gap-2 items-center text-[11px]">
                        <span
                            class="flex-shrink-0 bg-gray-400 text-white w-5 h-5 flex items-center justify-center rounded font-medium">忌</span>
                        <p class="text-gray-500 truncate flex-1" :title="activeInfo.avoid">
                            {{ activeInfo.avoid }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from 'vue';
import { Solar, HolidayUtil } from 'lunar-javascript';

const today = new Date();
const currentYear = ref(today.getFullYear());
const currentMonth = ref(today.getMonth() + 1);
const selectedDate = ref('');
const activeInfo = ref(null);
const weeks = ['一', '二', '三', '四', '五', '六', '日'];

const yearOptions = computed(() => {
    const start = today.getFullYear() - 20;
    return Array.from({ length: 41 }, (_, i) => start + i);
});

const calendarDays = computed(() => {
    const days = [];
    const firstDaySolar = Solar.fromYmd(currentYear.value, currentMonth.value, 1);
    const lunar = firstDaySolar.getLunar();
    const monthDaysCount = typeof lunar.getMonthDays === 'function' ? lunar.getMonthDays() : 30;

    let startWeek = firstDaySolar.getWeek();
    if (startWeek === 0) startWeek = 7;

    for (let i = startWeek - 1; i > 0; i--) {
        days.push(generateDay(firstDaySolar.next(-i), false));
    }
    for (let i = 0; i < monthDaysCount; i++) {
        days.push(generateDay(firstDaySolar.next(i), true));
    }
    const nextCount = 42 - days.length;
    for (let i = 0; i < nextCount; i++) {
        days.push(generateDay(firstDaySolar.next(monthDaysCount + i), false));
    }
    return days;
});

function generateDay(solar, isCurrent) {
    const lunar = solar.getLunar();
    const holiday = HolidayUtil.getHoliday(solar.getYear(), solar.getMonth(), solar.getDay());

    let lunarText = lunar.getDayInChinese();
    let isFestival = false;
    const fes = solar.getFestivals().concat(lunar.getFestivals());
    const jq = lunar.getJieQi();

    if (fes.length > 0) { lunarText = fes[0]; isFestival = true; }
    else if (jq) { lunarText = jq; isFestival = true; }
    else if (lunarText === '初一') { lunarText = lunar.getMonthInChinese() + '月'; }

    return {
        day: solar.getDay(),
        fullDate: solar.toYmd(),
        isCurrentMonth: isCurrent,
        isToday: solar.toYmd() === Solar.fromDate(today).toYmd(),
        lunarText,
        isFestival,
        isRest: holiday && !holiday.isWork(),
        isWork: holiday && holiday.isWork(),
        details: {
            lunarMonth: lunar.getMonthInChinese(),
            lunarDay: lunar.getDayInChinese(),
            ganZhi: lunar.getYearInGanZhi(),
            zodiac: typeof lunar.getYearShengXiao === 'function' ? lunar.getYearShengXiao() : lunar.getYearZodiac(),
            suit: (lunar.getDayYi ? lunar.getDayYi() : lunar.getSuit()).join(' '),
            avoid: (lunar.getDayJi ? lunar.getDayJi() : lunar.getAvoid()).join(' ')
        }
    };
}

const selectDay = (item) => {
    selectedDate.value = item.fullDate;
    activeInfo.value = item.details;
};

const prevMonth = () => {
    if (currentMonth.value === 1) { currentYear.value--; currentMonth.value = 12; }
    else { currentMonth.value--; }
};

const nextMonth = () => {
    if (currentMonth.value === 12) { currentYear.value++; currentMonth.value = 1; }
    else { currentMonth.value++; }
};

const goToday = () => {
    currentYear.value = today.getFullYear();
    currentMonth.value = today.getMonth() + 1;
    selectedDate.value = Solar.fromDate(today).toYmd();
    updateCalendar();
};

const updateCalendar = () => {
    const current = calendarDays.value.find(d => d.fullDate === selectedDate.value);
    if (current) activeInfo.value = current.details;
};

onMounted(() => goToday());
</script>